import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

const DEFAULT_FEATURES = {
  bottle_locker: true,
  loyalty_points: true,
  stamp_cards: true,
  qr_menu: true,
  campaigns: true,
  feedback: true,
  call_waiter: false,
  ordering: false,
  ordering_qr: false,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // skip /me check during Google OAuth callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Re-fetch auth when the user switches back to this tab so feature flag
  // changes made in Superadmin are reflected without a manual page reload.
  useEffect(() => {
    const handleFocus = () => {
      if (user) checkAuth();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && user) {
        checkAuth();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [user, checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    window.location.href = "/login";
  };

  // Computed feature flags — merge defaults with venue overrides
  const venueFeatures = user?.venue?.features
    ? { ...DEFAULT_FEATURES, ...user.venue.features }
    : DEFAULT_FEATURES;

  const hasFeature = (name) => Boolean(venueFeatures[name] ?? DEFAULT_FEATURES[name] ?? true);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refresh: checkAuth, venueFeatures, hasFeature }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
